/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentProps } from 'preact';
import { ViewHideIcon } from '#PRIVATE_ThemedIcons/ViewHideIcon';
import { ViewIcon } from '#PRIVATE_ThemedIcons/ViewIcon';
import { IconSwitchButton } from '#PRIVATE_IconSwitchButton';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { TestIdProps } from '#hooks/UNSAFE_useTestId';

type IconSwitchButtonProps = ComponentProps<typeof IconSwitchButton>;

type RevealToggleIconProps = TestIdProps & {
  /**
   * The aria label of the toggle.
   */
  accessibleLabel: IconSwitchButtonProps['accessibleLabel'];
  /**
   * Specifies whether the content is shown or hidden.
   */
  isRevealed?: boolean;
  /**
   * Property that triggers a callback immediately when toggle happens and value of isRevealed property should be updated
   */
  onToggle?: IconSwitchButtonProps['onToggle'];
  /**
   * Tooltip text to indicate that the toggle will hide the content.
   */
  tooltipHide?: string;
  /**
   * Tooltip text to indicate that the toggle will show the content.
   */
  tooltipShow?: string;
};

/**
 * RevealToggleIcon is a controlled toggle icon component with tooltip that displays
 * hide or show icons. It is used internally for input components that hide and show the content.
 */
export function RevealToggleIcon({
  accessibleLabel,
  isRevealed,
  onToggle,
  testId,
  tooltipHide,
  tooltipShow
}: RevealToggleIconProps) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const hide = tooltipHide || translations.formControl_hide();
  const show = tooltipShow || translations.formControl_show();

  return (
    <IconSwitchButton
      // we want to display the tooltip title of the expected action on toggle
      tooltip={isRevealed ? hide : show}
      accessibleLabel={accessibleLabel}
      // denote that the switch is selected when in the "masked" state
      isSelected={!isRevealed}
      onToggle={onToggle}
      testId={testId}
      size="sm">
      {/* we want to display the icon of the expected action on toggle */}
      {isRevealed ? <ViewHideIcon /> : <ViewIcon />}
    </IconSwitchButton>
  );
}
